import { CaseData, GameState, PendingEvent, VerdictOutcome, VerdictType } from './types';

const INITIAL_KPI = 50;
const KPI_MIN = 0;
const KPI_MAX = 100;
const RISK_MIN = 0;
const RISK_MAX = 100;

// Neutral fallback when a verdict has no defined outcome in the case
const NEUTRAL_OUTCOME: VerdictOutcome = {
  kpiDelta: 0,
  careerRiskDelta: 0,
  proceduralRiskDelta: 0,
  feedbackZh: '已记录。',
  feedbackEn: 'Noted.',
};

export function createInitialState(): GameState {
  return {
    currentDay: 1,
    dailyQuota: 5,
    processedToday: 0,
    totalProcessed: 0,
    kpi: INITIAL_KPI,
    careerRisk: 0,
    proceduralRisk: 0,
    pendingEvents: [],
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function getVerdictOutcome(caseData: CaseData, verdict: VerdictType): VerdictOutcome {
  return caseData.verdictOutcomes[verdict] ?? NEUTRAL_OUTCOME;
}

export function applyVerdict(
  state: GameState,
  verdict: VerdictType,
  caseData: CaseData
): GameState {
  const outcome = getVerdictOutcome(caseData, verdict);
  const nextTotalProcessed = state.totalProcessed + 1;

  // Queue any delayed events triggered by this verdict
  const newPendingEvents: PendingEvent[] = caseData.delayedEvents
    .filter(e => e.triggerVerdict === verdict)
    .map(e => ({
      ...e,
      firesAtCaseIndex: nextTotalProcessed + e.delayInCases,
    }));

  return {
    ...state,
    processedToday: state.processedToday + 1,
    totalProcessed: nextTotalProcessed,
    kpi: clamp(state.kpi + outcome.kpiDelta, KPI_MIN, KPI_MAX),
    careerRisk: clamp(state.careerRisk + outcome.careerRiskDelta, RISK_MIN, RISK_MAX),
    proceduralRisk: clamp(state.proceduralRisk + outcome.proceduralRiskDelta, RISK_MIN, RISK_MAX),
    pendingEvents: [...state.pendingEvents, ...newPendingEvents],
  };
}

// Returns [updatedState, eventsTriggered]
export function processDueEvents(state: GameState): [GameState, PendingEvent[]] {
  const due = state.pendingEvents.filter(e => e.firesAtCaseIndex <= state.totalProcessed);
  const remaining = state.pendingEvents.filter(e => e.firesAtCaseIndex > state.totalProcessed);

  if (due.length === 0) return [state, []];

  const newState = due.reduce(
    (s, e) => ({
      ...s,
      kpi: clamp(s.kpi + e.kpiDelta, KPI_MIN, KPI_MAX),
      careerRisk: clamp(s.careerRisk + e.careerRiskDelta, RISK_MIN, RISK_MAX),
      proceduralRisk: clamp(s.proceduralRisk + e.proceduralRiskDelta, RISK_MIN, RISK_MAX),
    }),
    { ...state, pendingEvents: remaining }
  );

  return [newState, due];
}

export function isDayComplete(state: GameState): boolean {
  return state.processedToday >= state.dailyQuota;
}

export function advanceDay(state: GameState): GameState {
  return {
    ...state,
    currentDay: state.currentDay + 1,
    processedToday: 0,
  };
}

// Game over conditions
export function isGameOver(state: GameState): boolean {
  return state.kpi <= KPI_MIN || state.careerRisk >= RISK_MAX || state.proceduralRisk >= RISK_MAX;
}

export type GameOverReason = 'kpi_zero' | 'career_risk' | 'procedural_risk' | 'all_cases_completed';

export function getGameOverReason(state: GameState): GameOverReason | null {
  if (state.kpi <= KPI_MIN) return 'kpi_zero';
  if (state.careerRisk >= RISK_MAX) return 'career_risk';
  if (state.proceduralRisk >= RISK_MAX) return 'procedural_risk';
  return null;
}
